import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { getIncidentSummary, type IncidentWire } from '../../api/client'
import { useToast } from '../../design/Toast'
import { usePermissions } from '../../shell/PermissionsContext'
import { useIncidentStream } from './useIncidentStream'

/**
 * The bell in the header: how many incidents are open and nobody has acknowledged, red when any of
 * them is high urgency. It also holds the live stream every alert page relies on, and says so in a
 * toast when a high-urgency incident opens while someone is looking at the portal.
 *
 * Nothing is shown when nothing is open: a permanent "0" is noise people learn to ignore.
 */
export function AlertsBadge() {
  const { hasPermission } = usePermissions()
  const allowed = hasPermission('device.view')
  const toast = useToast()
  const announced = useRef(new Set<string>())

  const summary = useQuery({
    queryKey: ['incident-summary'],
    queryFn: getIncidentSummary,
    enabled: allowed,
    retry: false,
    // The stream refreshes it at once; this only catches what a dropped stream missed.
    refetchInterval: 120_000,
  })

  useIncidentStream(allowed, (incident: IncidentWire) => {
    const fresh = Date.now() - new Date(incident.opened_at).getTime() < 5 * 60_000
    if (incident.state !== 'triggered' || incident.urgency !== 'high' || incident.parent_id || !fresh) return
    if (announced.current.has(incident.id)) return
    announced.current.add(incident.id)
    toast.error(
      <Link to={`/alerts/${incident.id}`} className="underline">
        {incident.title}
      </Link>,
    )
  })

  const open = summary.data?.triggered ?? 0
  if (!allowed || open === 0) return null
  const high = (summary.data?.triggered_high ?? 0) > 0
  return (
    <Link
      to="/alerts"
      className={
        'flex items-center gap-xs rounded-full px-sm py-xs text-xs font-medium ' +
        (high ? 'bg-danger-bg text-danger' : 'bg-warning-bg text-warning')
      }
      aria-label={`${open} open ${open === 1 ? 'alert' : 'alerts'} nobody has acknowledged`}
    >
      <Bell className="h-3.5 w-3.5" aria-hidden />
      {open} <span className="hidden sm:inline">open</span>
    </Link>
  )
}
